import React, { FunctionComponent, useState, useEffect, useCallback, useMemo } from "react"
import InfiniteScroll from "react-infinite-scroller"
import { useTranslation } from "react-i18next"
import PQueue from "p-queue"

import noPicturesImg from "assets/img/no-pictures.svg"

import Text from "components/Text/Text"
import ImageCollectionModel, { IImage } from "models/ImageCollection"
import ImageCollection from "components/ImageCollection/ImageCollection"
import { IApiLink, ICreateImageFile, Guid } from "@/types"
import AssetService from "services/AssetService"
import { IPictureGalleryItem } from "components/PictureGallery/PictureGallery"
import FileService from "services/FileService"
import toastMessages from "stores/toastMessages"
import Button from "components/Button/Button"
import Spinner from "components/Spinner/Spinner"
import PictureGalleryModal from "components/PictureGalleryModal/PictureGalleryModal"
import AddPicturesModal, { IFileToAdd } from "components/AddPicturesModal/AddPicturesModal"
import usePagedData from "hooks/usePagedData"
import { Flex, FlexItem } from "components/Flex"
import dayjs from "utils/dayjs"

interface IAssetPicturesProps {
    assetId: Guid
    lmid: string
    scrollEl?: React.RefObject<HTMLDivElement | null>
    hideTitle?: boolean
    readOnly?: boolean
}

const AssetPictures: FunctionComponent<React.PropsWithChildren<IAssetPicturesProps>> = ({
    assetId,
    lmid,
    scrollEl,
    hideTitle = false,
    readOnly = false,
}) => {
    const { t } = useTranslation()
    const [showPictureGalleryModal, setShowPictureGalleryModal] = useState(false)
    const [pictureGalleryActiveIndex, setPictureGalleryActiveIndex] = useState<number>(0)
    const [showAddPicturesModal, setShowAddPicturesModal] = useState(false)
    const [selectPicturesMode, setSelectPicturesMode] = useState(false)
    const [selectedPictures, setSelectedPictures] = useState<IImage[]>([])
    const [isDownloadingZip, setIsDownloadingZip] = useState(false)

    const assetImagesParams = useMemo(() => {
        return {
            assetId,
            pageSize: 10,
        }
    }, [assetId])

    const assetsCall = useCallback(() => {
        return AssetService.getAssetImages(assetImagesParams)
    }, [assetImagesParams])

    const {
        items: assetImages,
        loadNextPage: loadMoreImages,
        isInitialLoad: isInitialLoadAssetImages,
        isLoading: isLoadingImages,
        hasMore: hasMoreImages,
        refetchData: refetchAssetImages,
    } = usePagedData<ImageCollectionModel>(assetsCall, toastMessages.showSystemError)

    const pictureGalleryItems: IPictureGalleryItem[] = useMemo(() => {
        if (!assetImages) return []

        const items: IPictureGalleryItem[] = []

        let index = 0
        for (let collection of assetImages) {
            if (!collection.images) continue
            for (let image of collection.images) {
                items.push({
                    index: index++,
                    image,
                    date: collection.collectionDate,
                    tags: collection.tags
                })
            }
        }

        return items
    }, [assetImages])

    useEffect(() => {
        if (!showPictureGalleryModal) return

        // During refetch usePagedData clears items temporarily; keep modal open until loading finishes.
        if (isLoadingImages || assetImages === null) return

        if (pictureGalleryItems.length === 0) {
            setShowPictureGalleryModal(false)
            return
        }

        setPictureGalleryActiveIndex(prev => Math.min(prev, pictureGalleryItems.length - 1))
    }, [assetImages, isLoadingImages, showPictureGalleryModal, pictureGalleryItems.length])

    function handleToggleChangePictureMode() {
        setIsDownloadingZip(false)
        if (selectPicturesMode) {
            setSelectedPictures([])
            setSelectPicturesMode(false)
            return
        }

        setSelectPicturesMode(true)
    }

    async function handleDownloadMultipleImages() {
        setIsDownloadingZip(true)
        if (selectedPictures.length === 0) {
            handleToggleChangePictureMode()
            return
        }

        const fileNameWithoutExtension = `${t("asset.pictures")}_${dayjs().format("YYYY-MM-DD")}_${lmid.replace(/:/g, "_")}`
        try {
            await FileService.downloadZip({
                fileNameWithoutExtension,
                files: selectedPictures.map(img => ({
                    fileId: img.fileId,
                    friendlyName: img.friendlyName,
                }))
            })
        } catch (e) {
            toastMessages.show({ title: t('message.systemError'), type: "error" })
        }
        finally {
            handleToggleChangePictureMode()
        }
    }

    function handleImageCollectionImageClick(clickedImageCollectionIndex: number, clickedImageIndex: number) {
        if (!assetImages) return

        if (selectPicturesMode) {
            handlePictureSelected(clickedImageCollectionIndex, clickedImageIndex)
            return
        }

        let index = 0
        let newPictureGalleryActiveIndex = 0
        for (let [collectionIndex, collection] of assetImages.entries()) {
            if (!collection.images) continue
            for (let [imageIndex] of collection.images.entries()) {
                if (collectionIndex === clickedImageCollectionIndex && imageIndex === clickedImageIndex) {
                    newPictureGalleryActiveIndex += index
                }
                index++
            }
        }

        setPictureGalleryActiveIndex(newPictureGalleryActiveIndex)
        setShowPictureGalleryModal(true)
    }

    function handlePictureSelected(clickedImageCollectionIndex: number, clickedImageIndex: number) {
        setSelectedPictures(prevSelectedPictures => {
            const newSelectedPictures = [...prevSelectedPictures]
            const clickedImage = assetImages?.[clickedImageCollectionIndex]?.images?.[clickedImageIndex]

            if (!clickedImage) return newSelectedPictures

            const existingIndex = newSelectedPictures.findIndex(image => image === clickedImage)

            if (existingIndex > -1) {
                newSelectedPictures.splice(existingIndex, 1)
            } else {
                newSelectedPictures.push(clickedImage)
            }

            return newSelectedPictures
        })
    }

    function handleSelectAllPicturesForCollection(clickedImageCollectionIndex: number) {
        setSelectedPictures(prevSelectedPictures => {
            const newSelectedPictures = [...prevSelectedPictures]
            const imagesToAdd = assetImages?.[clickedImageCollectionIndex]?.images

            if (!imagesToAdd) return newSelectedPictures

            imagesToAdd.forEach(img => {
                if (img && !newSelectedPictures.includes(img)) {
                    newSelectedPictures.push(img)
                }
            })

            return newSelectedPictures
        })
    }

    async function handleImageCollectionCreate(files: IFileToAdd[], tags: string[]) {
        const queue = new PQueue({ concurrency: 1 })
        const fileIds: Guid[] = []

        const uploadFile = async (file: File) => {
            try {
                const uploadedFile = await FileService.uploadFile<ICreateImageFile>(file)
                fileIds.push(uploadedFile.id)
            } catch (e) {
                toastMessages.show({ title: t('message.systemError'), type: "error" })
            }
        }

        for (let file of files) {
            queue.add(uploadFile.bind(null, file.file))
        }

        await queue.onIdle()

        if (fileIds.length === 0) {
            throw new Error()
        }

        try {
            await AssetService.createAssetImageCollection({
                assetId,
                fileIds,
                tags,
            })
        } catch (e) {
            toastMessages.show({ title: t('message.systemError'), type: "error" })
            throw new Error()
        }

        refetchAssetImages()
    }

    async function handleImageDelete(link: IApiLink, deletedItemIndex: number) {
        try {
            setPictureGalleryActiveIndex(prev => {
                const validDeletedIndex = Number.isFinite(deletedItemIndex) ? deletedItemIndex : prev
                const currentLength = pictureGalleryItems.length

                if (currentLength <= 1) {
                    return 0
                }

                // Move to next preview when available; if deleting the last item, show previous.
                return validDeletedIndex >= currentLength - 1
                    ? currentLength - 2
                    : validDeletedIndex
            })
            await AssetService.deleteAssetImage(link)
            refetchAssetImages()
        } catch (e) {
            toastMessages.show({ title: t('message.systemError'), type: "error" })
        }
    }

    const addPicturesButton = !readOnly ? (
        <Button
            theme="secondary"
            label={t('asset.addPictures')}
            onClick={() => setShowAddPicturesModal(true)} />
    ) : null

    return (
        <>
            <InfiniteScroll
                pageStart={0}
                loadMore={loadMoreImages}
                initialLoad={isInitialLoadAssetImages}
                hasMore={hasMoreImages}
                getScrollParent={() => scrollEl?.current || null}
                useWindow={false}>

                {!hideTitle && (
                    <Flex alignItems="center" justifyContent="space-between" className="mb-3" gap="2.4rem">
                        <Text type="h3">{t("common.pictures")}</Text>
                        {assetImages !== null && assetImages.length > 0 && !selectPicturesMode && !readOnly &&
                            <Button
                                theme="link"
                                label={t('asset.downloadMultiplePictures')}
                                onClick={handleToggleChangePictureMode}
                            />
                        }
                    </Flex>
                )}
                {assetImages && assetImages.length > 0 &&
                    <>
                        {addPicturesButton && (
                            <div className="mb-8">
                                {addPicturesButton}
                            </div>
                        )}
                        {assetImages.map((imageCollection, idx) =>
                            <ImageCollection
                                key={idx}
                                data={imageCollection}
                                info
                                tags
                                imageSelectMode={selectPicturesMode}
                                selectedImageIds={selectedPictures.map(img => img.fileId)}
                                onImageClick={imageIndex => handleImageCollectionImageClick(idx, imageIndex)}
                                onSelectAll={() => handleSelectAllPicturesForCollection(idx)} />)}
                    </>
                }
                {isLoadingImages && <Spinner className="py-4" />}
                {!isLoadingImages && assetImages && assetImages.length === 0 &&
                    <div className="text-center py-4">
                        <img src={noPicturesImg} alt="" className="mb-6" />
                        <Text type="p" className="white-space-pre-line mb-4">{t('asset.noPicturesAvailable')}</Text>
                        {addPicturesButton}
                    </div>
                }

                {selectPicturesMode &&
                    <Flex className="border-top-grey-100 pt-3" alignItems="center" justifyContent="space-between">
                        <Text className="color-grey-500">{t('asset.xPicturesSelected', { count: selectedPictures.length })}</Text>
                        <FlexItem>
                            <Button
                                theme="secondary"
                                label={t('common.cancel')}
                                width="auto"
                                disabled={isDownloadingZip}
                                onClick={handleToggleChangePictureMode}
                            />
                            <Button
                                label={t('common.download')}
                                width="auto"
                                className="ml-2"
                                loading={isDownloadingZip}
                                disabled={isDownloadingZip}
                                onClick={handleDownloadMultipleImages} />
                        </FlexItem>
                    </Flex>}
            </InfiniteScroll>
            {showPictureGalleryModal &&
                <PictureGalleryModal
                    pictureGallery={{
                        items: pictureGalleryItems,
                        activeIndex: pictureGalleryActiveIndex,
                        loadMore: hasMoreImages ? loadMoreImages : undefined,
                        onDelete: handleImageDelete,
                    }}
                    onClose={() => setShowPictureGalleryModal(false)}
                />
            }
            {showAddPicturesModal &&
                <AddPicturesModal
                    minPicturesCount={1}
                    onSave={handleImageCollectionCreate}
                    onClose={() => setShowAddPicturesModal(false)}
                />
            }
        </>
    )
}

export default AssetPictures
